import React from 'react';

interface JobProgressProps {
    loadingStage?: string | null;
    loadingMessage?: string | null;
    currentDayCount?: number | null;
    totalDayCount?: number | null;
    progressPct?: number | null;
    onCancel?: (() => void) | null;
    cancelLabel?: string;
    cancelDisabled?: boolean;
    style?: React.CSSProperties;
}

export function JobProgress({
    loadingStage,
    loadingMessage,
    currentDayCount,
    totalDayCount,
    progressPct,
    onCancel,
    cancelLabel = 'Cancel',
    cancelDisabled = false,
    style,
}: JobProgressProps) {
    const stageLabelMap: Record<string, string> = {
        queued: 'Queued',
        loading_day_cache: 'Loading calibration days',
        cross_validating: 'Cross-validating model',
        preparing_backtest: 'Preparing backtest',
        backtesting: 'Running backtest',
        computing_diagnostics: 'Computing diagnostics',
        finalizing: 'Finalizing result',
        completed: 'Completed',
        failed: 'Failed',
        cancelled: 'Cancelled',
    };
    const progressText = typeof currentDayCount === 'number' && typeof totalDayCount === 'number'
        ? `${currentDayCount}/${totalDayCount} days`
        : null;
    const progressValue = typeof progressPct === 'number' ? Math.max(0, Math.min(100, progressPct)) : null;

    return (
        <div style={{
            minWidth: '280px',
            padding: '0.65rem 0.85rem',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-glass)',
            border: '1px solid var(--surface-glass-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4rem',
            ...style,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.8rem' }}>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                    {loadingStage ? (stageLabelMap[loadingStage] ?? loadingStage.replaceAll('_', ' ')) : 'MeepMeep'}
                </span>
                {progressText && (
                    <span style={{ color: 'var(--text-secondary)' }}>{progressText}</span>
                )}
            </div>
            <div style={{
                width: '100%',
                height: '8px',
                borderRadius: '999px',
                background: 'rgba(255,255,255,0.08)',
                overflow: 'hidden',
            }}>
                <div style={{
                    width: `${progressValue ?? 0}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-success))',
                    transition: 'width 0.2s ease',
                }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', flex: 1 }}>
                    {loadingMessage ?? 'Preparing MeepMeep job...'}
                </div>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={cancelDisabled}
                        style={{
                            border: '1px solid rgba(231, 76, 60, 0.4)',
                            background: cancelDisabled ? 'rgba(231, 76, 60, 0.08)' : 'rgba(231, 76, 60, 0.14)',
                            color: cancelDisabled ? 'rgba(255, 170, 170, 0.7)' : '#ffb0a9',
                            borderRadius: '999px',
                            padding: '0.28rem 0.7rem',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            cursor: cancelDisabled ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {cancelLabel}
                    </button>
                )}
            </div>
        </div>
    );
}
